import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/app/Header";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Shield, Trash2, Check, X, Loader2, MessageSquare, Heart, Flag, Users, Download, PoundSterling, TrendingUp, ExternalLink, Mail, Copy } from "lucide-react";
import AffiliateTrendsCard from "../components/app/AffiliateTrendsCard";
import PhotoModerationCard from "../components/app/PhotoModerationCard";
import DeployBootstrapCard from "../components/app/DeployBootstrapCard";
import DeploySyncChecklist from "../components/app/DeploySyncChecklist";
import PreviewSyncCard from "../components/app/PreviewSyncCard";
import ConversionFunnelCard from "../components/app/ConversionFunnelCard";
import InstagramPublisherCard from "../components/app/InstagramPublisherCard";
import EnrichmentOverviewCard from "../components/app/EnrichmentOverviewCard";
import PitchupIngestCard from "../components/app/PitchupIngestCard";
import BetaCodesCard from "../components/app/BetaCodesCard";
import EmailCard from "../components/app/EmailCard";
import PriceReportsCard from "../components/app/PriceReportsCard";
import WhatsNewBroadcastCard from "../components/app/WhatsNewBroadcastCard";
import RestoreLlynGwynantCard from "../components/app/RestoreLlynGwynantCard";
import MetaAutoresponderCard from "../components/app/MetaAutoresponderCard";
import FestivalPollCard from "../components/app/FestivalPollCard";
import BouncedEmailCard from "../components/app/BouncedEmailCard";
import ThreadConvertCard from "../components/app/ThreadConvertCard";
import BurnEnrichmentCard from "../components/app/BurnEnrichmentCard";
import PromoAdminCard from "../components/app/PromoAdminCard";
import PromoCodesCard from "../components/app/PromoCodesCard";
import FeedbackInboxCard from "../components/app/FeedbackInboxCard";
import EnrichFactsCard from "../components/app/EnrichFactsCard";
import EnrichCompletenessCard from "../components/app/EnrichCompletenessCard";
import WebsiteReportsCard from "../components/app/WebsiteReportsCard";
import PresenceStatsCard from "../components/app/PresenceStatsCard";
import PrintPackCard from "../components/app/PrintPackCard";
import OSMBulkImportCard from "../components/app/OSMBulkImportCard";
import AdminMigrationsCard from "../components/app/AdminMigrationsCard";
import DuplicateReviewCard from "../components/app/DuplicateReviewCard";
import UserManagementCard from "../components/app/UserManagementCard";import PollHistoryCard from "../components/app/PollHistoryCard";
import DeployStatusCard from "../components/app/DeployStatusCard";
import BulkEnrichCard from "../components/app/BulkEnrichCard";
import EnrichRegionProgressCard from "../components/app/EnrichRegionProgressCard";
import LaunchChecklistCard from "../components/app/LaunchChecklistCard";
import LaunchFireCard from "../components/app/LaunchFireCard";
import MarketingSnippetsCard from "../components/app/MarketingSnippetsCard";
import FriendsFamilyShareCard from "../components/app/FriendsFamilyShareCard";
import AutoEnrichStatusCard from "../components/app/AutoEnrichStatusCard";
import MaintenanceCard from "../components/app/MaintenanceCard";
import FisheriesPrewarmCard from "../components/app/FisheriesPrewarmCard";
import CharityDonationsAdminCard from "../components/app/CharityDonationsAdminCard";
import AffiliatePartnersCard from "../components/app/AffiliatePartnersCard";
import AffiliatePromoAdminCard from "../components/app/AffiliatePromoAdminCard";
import BookingConversionCard from "../components/app/BookingConversionCard";
import ProDealsCard from "../components/app/ProDealsCard";
import FeaturedReelCard from "../components/app/FeaturedReelCard";
import InstagramReelsCard from "../components/app/InstagramReelsCard";
import EmailDiagnosticsCard from "../components/app/EmailDiagnosticsCard";
import WalksReviewCard from "../components/app/WalksReviewCard";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [partnership, setPartnership] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [importing, setImporting] = useState(false);

  const osmImport = async () => {
    if (!window.confirm("Import up to 25 UK camp_sites from OpenStreetMap? Duplicates are skipped.")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/ingest/osm?limit=25");
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped} (of ${data.total_scanned} scanned)`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImporting(false); }
  };

  const load = async (st = filter) => {
    setLoading(true);
    try {
      const [s, r, e, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/reports${st && st !== "all" ? `?status=${st}` : ""}`),
        api.get("/admin/affiliate/earnings").catch(() => ({ data: null })),
        api.get("/admin/affiliate/partnership").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setReports(r.data);
      setEarnings(e.data);
      setPartnership(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const setPartnershipStage = async (status) => {
    try {
      const { data } = await api.put("/admin/affiliate/partnership", {
        status,
        partner: partnership?.partner || "pitchup",
      });
      setPartnership({ ...partnership, ...data });
      toast.success(`Marked as ${status.replace("_", " ")}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { toast.error("Admin only"); nav("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  useEffect(() => { if (user && user.role === "admin") load(filter); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteContent = async (type, id) => {
    if (!window.confirm(`Delete this ${type}? This cannot be undone.`)) return;
    try {
      await api.post("/admin/content/delete", { target_type: type, target_id: id });
      toast.success(`${type} removed`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (authLoading || !user || user.role !== "admin") {
    return <div className="min-h-screen"><Header /><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="admin-page">
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="eyebrow mb-3 flex items-center gap-2"><Shield className="w-3 h-3" /> 04 · Moderation</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-8">Admin Dashboard</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-0 border hairline bg-white mb-10" data-testid="admin-stats">
            <Stat label="Members"        value={stats.users}            hint="People signed up" />
            <Stat label="Campsites"      value={stats.campsites}        hint="Pitches in the directory" border />
            <Stat label="Forum posts"    value={stats.threads}          hint="Topics started in Campfire Chat" border />
            <Stat label="Comments"       value={stats.replies}          hint="Replies under those topics" border />
            <Stat label="Saved pitches"  value={stats.favorites}        hint='Campers who tapped ♥ "save for later"' border />
            <Stat label="Partner clicks" value={stats.affiliate_clicks} hint="Taps that left for a booking" border />
            <Stat label="Reports to review" value={stats.reports_open}  hint="Flagged content awaiting you" border accent />
          </div>
        )}

        <DeployStatusCard />

        <PreviewSyncCard />

        <BookingConversionCard />

        <PresenceStatsCard />

        <ConversionFunnelCard />

        <Link
          to="/admin/pinterest-pins"
          className="flex items-center justify-between gap-3 bg-white border hairline p-4 mb-4 hover:border-[#ff4500] hover:bg-[#fff4ee] transition-colors group"
          data-testid="admin-pinterest-link"
        >
          <div className="min-w-0">
            <div className="eyebrow flex items-center gap-2 mb-0.5">
              📌 Free advertising · Pinterest pin pack
            </div>
            <div className="font-display text-base mb-0.5">30 ready-to-upload Pinterest pins</div>
            <div className="text-[11px] text-[#666] leading-snug">
              Auto-generated 1000×1500 pins of your featured campsites.
            </div>
          </div>
          <div className="text-[#ff4500] font-bold uppercase tracking-wider text-[10px] mono group-hover:translate-x-1 transition-transform flex-shrink-0">
            Open →
          </div>
        </Link>

        <AdminSection title="Launch & Marketing" testid="admin-section-marketing" defaultOpen={false}>
          <InstagramPublisherCard />
          <LaunchChecklistCard />
          <LaunchFireCard />
          <MarketingSnippetsCard />
          <FriendsFamilyShareCard />
          <WhatsNewBroadcastCard />
          <FeaturedReelCard />
          <MetaAutoresponderCard />
          <FestivalPollCard />
          <PromoAdminCard />
          <PromoCodesCard />
          <ProDealsCard />
          <InstagramReelsCard />
          <section className="border hairline rounded-2xl p-5 sm:p-6 mb-4 bg-gradient-to-br from-[#fff4ee] to-white" data-testid="admin-reels-link">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
                  <span>🎬</span> Marketing reels
                </div>
                <h2 className="font-display text-xl sm:text-2xl tracking-tight">Reels library</h2>
                <p className="text-xs text-[#666] mt-1 max-w-xl">
                  13 share-able 9:16 reels with pre-written captions.
                </p>
              </div>
              <Link
                to="/launch-pack"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
                data-testid="admin-reels-link-open"
              >
                Open library →
              </Link>
            </div>
          </section>
        </AdminSection>

        <AdminSection title="Data & Ingestion" testid="admin-section-data" defaultOpen={false}>
          <PitchupIngestCard />
          <OSMBulkImportCard />
          <DuplicateReviewCard />
          <WalksReviewCard />
          <WebsiteReportsCard />
          <PriceReportsCard />
          <AutoEnrichStatusCard />
          <FisheriesPrewarmCard />
          <BurnEnrichmentCard />
          <BulkEnrichCard />
          <EnrichRegionProgressCard />
          <EnrichCompletenessCard />
          <EnrichFactsCard />
          <PhotoModerationCard />
          <div className="mb-4 p-5 border hairline bg-white flex items-start justify-between gap-6 flex-wrap" data-testid="ingest-panel">
            <div>
              <div className="eyebrow mb-1">Data Ingestion</div>
              <div className="font-display text-lg mb-1">Import UK campsites from OpenStreetMap</div>
              <p className="text-xs text-[#555] max-w-xl">
                Fetches <code className="mono text-xs">tourism=camp_site</code> nodes across the UK via Overpass API.
              </p>
            </div>
            <button onClick={osmImport} disabled={importing} className="btn-primary inline-flex items-center gap-2" data-testid="osm-import">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Import 25 from OSM
            </button>
          </div>
        </AdminSection>

        <AdminSection title="Users & Moderation" testid="admin-section-moderation" defaultOpen={false}>
          <UserManagementCard />
          <FeedbackInboxCard />
          <BouncedEmailCard />
          <ThreadConvertCard />
        </AdminSection>

        <AdminSection title="Emails" testid="admin-section-emails" defaultOpen={false}>
          <EmailDiagnosticsCard />
          <EmailCard />
        </AdminSection>

        <AdminSection title="Site Management" testid="admin-section-site" defaultOpen={false}>
          <MaintenanceCard />
          <CharityDonationsAdminCard />
          <AffiliatePartnersCard />
          <AffiliatePromoAdminCard />
          <PollHistoryCard />
          <PrintPackCard />
          <DeploySyncChecklist />
          <AdminMigrationsCard />
          <DeployBootstrapCard />
          <BetaCodesCard />
          <RestoreLlynGwynantCard />
        </AdminSection>

        {earnings && <EarningsCard earnings={earnings} partnership={partnership} onStageChange={setPartnershipStage} />}

        {/* iter180 — Compact "AI progress at a glance" strip lives at
            the very bottom so it never fights the operational cards for
            visibility, but stays clickable when the operator wants to
            drill into any pipeline. */}
        <EnrichmentOverviewCard />

        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Reports queue</div>
          <div className="flex gap-2">
            {["open", "resolved", "dismissed", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${filter === f ? "bg-[#111] text-white border-[#111]" : "hairline bg-white"}`}
                data-testid={`filter-${f}`}>{f}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : reports.length === 0 ? (
          <div className="p-10 border hairline bg-white text-center text-sm text-[#888]">No reports in this view.</div>
        ) : (
          <div className="space-y-3">
            {reports.map((r, i) => (
              <div key={r.report_id} className="p-5 border hairline bg-white" data-testid={`report-row-${i}`}>
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-black/5 mono">{r.target_type}</span>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider mono ${r.status === "open" ? "bg-[#ffefea] text-[#ff4500]" : "bg-black/5"}`}>{r.status}</span>
                  <span className="mono text-xs text-[#555]">{fmt(r.created_at)}</span>
                </div>
                <div className="mb-3 p-3 bg-[#fafafa] border hairline">
                  <div className="eyebrow mb-1">Reported content</div>
                  {r.target ? (
                    <div className="text-sm">
                      {r.target.title && <div className="font-display text-base">{r.target.title}</div>}
                      <p className="text-[#333] line-clamp-3">{r.target.body}</p>
                      <div className="mono text-xs text-[#888] mt-1">by {r.target.user_name}</div>
                    </div>
                  ) : <div className="text-sm text-[#888]">Target no longer exists</div>}
                </div>
                <div className="mb-3">
                  <div className="eyebrow mb-1">Reason</div>
                  <p className="text-sm">{r.reason}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setStatus(r.report_id, "resolved")}
                    className="btn-secondary !px-3 !py-2 text-[11px] flex items-center gap-1.5" data-testid={`resolve-${i}`}>
                    <Check className="w-3 h-3" /> Resolve
                  </button>
                  <button onClick={() => setStatus(r.report_id, "dismissed")}
                    className="btn-secondary !px-3 !py-2 text-[11px] flex items-center gap-1.5" data-testid={`dismiss-${i}`}>
                    <X className="w-3 h-3" /> Dismiss
                  </button>
                  {r.target && (
                    <button onClick={() => deleteContent(r.target_type, r.target_id)}
                      className="!px-3 !py-2 text-[11px] flex items-center gap-1.5 bg-[#111] text-white border border-[#111]"
                      data-testid={`delete-${i}`}>
                      <Trash2 className="w-3 h-3" /> Delete {r.target_type}
                    </button>
                  )}
                  {r.target_type === "thread" && (
                    <Link to={`/community/${r.target_id}`}
                      className="!px-3 !py-2 text-[11px] flex items-center gap-1.5 hairline bg-white" data-testid={`view-${i}`}>
                      <MessageSquare className="w-3 h-3" /> View
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, hint, border, accent }) => (
  <div className={`p-5 ${border ? "border-l hairline" : ""} ${accent ? "bg-[#ffefea]" : ""}`}>
    <div className="eyebrow">{label}</div>
    <div className="font-display text-3xl mono mt-1">{value}</div>
    {hint && <div className="text-[10px] text-[#666] mt-1.5 leading-snug">{hint}</div>}
  </div>
);

/**
 * AdminSection — condenses the wall of admin cards into collapsible groups.
 * Uses native <details>/<summary> so keyboard/screen-reader users get the
 * expected disclosure semantics with zero JS. Each section persists its
 * open/closed state per browser via localStorage.
 */
function AdminSection({ title, testid, defaultOpen = false, children }) {
  const storageKey = `ccu:admin-section:${testid}`;
  const [open, setOpen] = React.useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch { /* ignore */ }
    return defaultOpen;
  });
  const toggle = (e) => {
    const isOpen = e.currentTarget.open;
    setOpen(isOpen);
    try { window.localStorage.setItem(storageKey, isOpen ? "1" : "0"); } catch { /* ignore */ }
  };
  return (
    <details
      className="mb-3 border hairline bg-white rounded-lg overflow-hidden"
      open={open}
      onToggle={toggle}
      data-testid={testid}
    >
      <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer list-none select-none hover:bg-[#fafafa] transition-colors">
        <div className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#888]">Section</span>
          <span className="font-display text-base">{title}</span>
        </div>
        <span className="mono text-[10px] uppercase tracking-wider text-[#ff4500]">
          {open ? "Hide" : "Show"}
        </span>
      </summary>
      <div className="px-4 pb-4 pt-2 border-t hairline bg-[#fafafa]/40">
        {children}
      </div>
    </details>
  );
}

function EarningsCard({ earnings, partnership, onStageChange }) {
  const e = earnings;
  const delta = e.clicks_last_month > 0
    ? Math.round(((e.clicks_this_month - e.clicks_last_month) / e.clicks_last_month) * 100)
    : (e.clicks_this_month > 0 ? 100 : 0);
  const positive = delta >= 0;

  return (
    <div
      className="mb-10 border hairline-strong bg-gradient-to-br from-[#fff4ee] to-white p-6 lg:p-8 relative overflow-hidden"
      data-testid="admin-earnings-card"
    >
      {/* Decorative accent */}
      <div className="absolute -right-8 -top-8 w-40 h-40 bg-[#ff4500]/10 rounded-full blur-2xl pointer-events-none" />

      <div className="flex items-start justify-between gap-6 flex-wrap mb-6 relative">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2 text-[#ff4500]">
            <Heart className="w-3 h-3 fill-[#ff4500]" /> Thank-you from your campers
          </div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight">
            £{e.projected_earnings_this_month_gbp.toFixed(2)}
            <span className="text-base text-[#555] ml-2 font-normal">projected this month</span>
          </h2>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider mono border ${positive ? "border-[#ff4500] text-[#ff4500] bg-white" : "border-[#888] text-[#555] bg-white"}`}
          data-testid="earnings-delta">
          <TrendingUp className={`w-3 h-3 ${positive ? "" : "rotate-180"}`} />
          {positive ? "+" : ""}{delta}% vs last month
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border hairline bg-white mb-6 relative">
        <MiniStat label="Clicks this month" value={e.clicks_this_month} testid="earnings-clicks-month" />
        <MiniStat label="Clicks last month" value={e.clicks_last_month} border testid="earnings-clicks-last" />
        <MiniStat label="All-time clicks" value={e.clicks_total} border testid="earnings-clicks-total" />
        <MiniStat
          label="Projected last month"
          value={`£${e.projected_earnings_last_month_gbp.toFixed(2)}`}
          border
          testid="earnings-last"
        />
      </div>

      {e.top_campsites_this_month.length > 0 && (
        <div className="mb-6 relative" data-testid="earnings-top-sites">
          <div className="eyebrow mb-3">Top earners this month</div>
          <div className="grid md:grid-cols-5 gap-0 border hairline bg-white">
            {e.top_campsites_this_month.map((c, i) => (
              <div key={c.campsite_id}
                className={`p-3 ${i > 0 ? "md:border-l hairline" : ""}`}
                data-testid={`top-site-${i}`}>
                <div className="text-xs mono text-[#555] truncate" title={c.name}>{c.name}</div>
                <div className="mono text-lg mt-1">{c.clicks} <span className="text-xs text-[#888]">click{c.clicks === 1 ? "" : "s"}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {partnership && (
        <PartnershipStatus
          partnership={partnership}
          onChange={onStageChange}
        />
      )}

      <PitchupEmailCTA earnings={e} />

      <AffiliateTrendsCard />

      <details className="text-xs text-[#555] relative mt-6" data-testid="earnings-assumptions">
        <summary className="cursor-pointer mono uppercase tracking-wider text-[#888] hover:text-[#ff4500]">
          How is this projected?
        </summary>
        <p className="mt-3 leading-relaxed max-w-2xl">
          <PoundSterling className="w-3 h-3 inline mr-1" />
          Clicks × {(e.assumptions.conversion_rate * 100).toFixed(0)}% conversion × £{e.assumptions.avg_booking_value_gbp.toFixed(0)} avg booking × {(e.assumptions.commission_rate * 100).toFixed(0)}% commission.{" "}
          <strong>These are UK affiliate-industry defaults.</strong> Once you sign an agreement with Pitchup (or Cool Camping / Hipcamp / Awin),
          update the constants in <code className="mono text-[11px] bg-black/5 px-1">/api/admin/affiliate/earnings</code> with your real rates for live earnings.
        </p>
        <p className="mt-2 max-w-2xl">
          <ExternalLink className="w-3 h-3 inline mr-1" />
          <a href="https://www.pitchup.com/about/faq/" target="_blank" rel="noopener noreferrer" className="link-underline text-[#ff4500]">
            Contact Pitchup
          </a>{" "}
          to request a private affiliate arrangement — they don't run a public program, but they do approve individual partners by email.
        </p>
      </details>
    </div>
  );
}

const MiniStat = ({ label, value, border, testid }) => (
  <div className={`p-4 ${border ? "md:border-l hairline" : ""}`} data-testid={testid}>
    <div className="eyebrow text-[10px]">{label}</div>
    <div className="font-display text-2xl mono mt-1">{value}</div>
  </div>
);

const STAGE_META = {
  prospecting: { label: "Prospecting", dot: "#888", desc: "Haven't reached out yet" },
  contacted: { label: "Contacted", dot: "#ff9260", desc: "Pitch email sent" },
  in_talks: { label: "In Talks", dot: "#ff4500", desc: "Reply received — negotiating" },
  live: { label: "Live", dot: "#22c55e", desc: "Earning commission" },
};

function PartnershipStatus({ partnership, onChange }) {
  const stages = partnership.stages || ["prospecting", "contacted", "in_talks", "live"];
  const currentIdx = stages.indexOf(partnership.status);

  return (
    <div className="mb-4 relative" data-testid="partnership-status">
      <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="eyebrow">Partnership status · Pitchup</div>
          <div className="text-xs text-[#555] mt-0.5">
            {STAGE_META[partnership.status]?.desc || "—"}
            {partnership.updated_at && (
              <span className="mono text-[#888] ml-2">
                · updated {new Date(partnership.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Segmented control with progress fill */}
      <div className="border hairline bg-white p-0 flex overflow-hidden" role="tablist">
        {stages.map((s, i) => {
          const meta = STAGE_META[s] || { label: s, dot: "#888" };
          const isCurrent = s === partnership.status;
          const isPast = i < currentIdx;
          return (
            <button
              key={s}
              role="tab"
              aria-selected={isCurrent}
              onClick={() => onChange(s)}
              className={`flex-1 px-3 py-3 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 transition-colors ${
                i > 0 ? "border-l hairline" : ""
              } ${isCurrent ? "bg-[#111] text-white" : isPast ? "bg-[#fff4ee] text-[#ff4500]" : "bg-white text-[#555] hover:bg-[#fafafa]"}`}
              data-testid={`partnership-stage-${s}`}
              title={meta.desc}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: isCurrent ? "#fff" : meta.dot }}
              />
              <span>{i + 1}. {meta.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PitchupEmailCTA({ earnings }) {
  const e = earnings;

  // Build a warm, evidence-led pitch email with your live numbers.
  const buildEmail = () => {
    const monthly = e.clicks_this_month;
    const total = e.clicks_total;
    const topLine = e.top_campsites_this_month.length > 0
      ? e.top_campsites_this_month.slice(0, 3).map(c => `   • ${c.name} — ${c.clicks} click${c.clicks === 1 ? "" : "s"}`).join("\n")
      : "   • (growing fast — drop me a line in a few weeks for an updated list)";

    const to = "info@pitchup.com";
    const subject = "Affiliate partnership enquiry — CampCompass UK (290 UK campsites)";
    const body = `Hi Pitchup team,

I'm the founder of CampCompass UK (https://camp-compass.uk) — a community-first UK campsite comparison app.
I'd love to explore a private affiliate partnership. A few numbers that hopefully make the case:

• Sites indexed:   290+ UK campsites (from OpenStreetMap, with community reviews & AI-curated descriptions)
• Click traffic:   ${monthly} "Book on Pitchup" clicks this month · ${total} all-time
• Conversion surfaces where Pitchup is the only booking partner (6 placements):
   - Browse list cards (primary orange CTA on every site)
   - Map pin popups
   - Side-by-side compare table (top-row CTA per column)
   - Individual campsite detail page (sticky mobile bar + primary sidebar CTA)
   - Saved-favourites page (with trip-date pre-fill)
• Top-clicked campsites this month:
${topLine}

I'm already deep-linking campers to pitchup.com/search/?q={site}+{region} — every click flows through an /api/affiliate/click endpoint on our side so traffic attribution is watertight once we have a tracking parameter or partner ID from you.

A couple of questions:
  1. What's the tracking-parameter format you use for private affiliates (e.g. ?aff=… or ?src=…)?
  2. What's the typical commission rate and payout schedule?
  3. Is there a preferred link format you'd like us to migrate to (e.g. /en-gb/{slug}/ instead of the search page)?

Happy to jump on a 15-minute call — or answer anything else that's useful.

Thanks so much,
[Your name]
Founder, CampCompass UK
`;
    return { to, subject, body };
  };

  const openMail = () => {
    const { to, subject, body } = buildEmail();
    const url = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const copyEmail = async () => {
    const { to, subject, body } = buildEmail();
    const text = `To: ${to}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copied — paste into Gmail, Outlook, anywhere.");
    } catch {
      toast.error("Couldn't copy — try the Open-in-mail button instead.");
    }
  };

  return (
    <div
      className="mb-0 p-5 border hairline bg-[#111] text-white flex items-start gap-4 flex-wrap relative"
      data-testid="pitchup-email-cta"
    >
      <div className="flex-1 min-w-[260px]">
        <div className="eyebrow text-[#ff9260] mb-1.5 flex items-center gap-2">
          <Mail className="w-3 h-3" /> Ready to go — 1-click pitch
        </div>
        <div className="font-display text-lg leading-tight mb-1">
          Email Pitchup for an affiliate deal
        </div>
        <p className="text-xs text-white/70 max-w-xl">
          We've pre-drafted the email using your live stats ({e.clicks_this_month} clicks this month, {e.clicks_total} all-time,
          top {Math.min(3, e.top_campsites_this_month.length)} sites). Hit the button, review, send from your own address.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={openMail}
          className="bg-[#ff4500] text-white px-4 py-3 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-2 hover:bg-[#e03d00] transition-colors shadow-[0_4px_0_0_#fff]"
          data-testid="pitchup-email-open"
        >
          <Mail className="w-4 h-4" /> Open in mail
        </button>
        <button
          onClick={copyEmail}
          className="bg-white/10 text-white border border-white/25 px-4 py-3 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-2 hover:bg-white/20 transition-colors"
          data-testid="pitchup-email-copy"
          title="Copy the full email — paste into Gmail, Outlook, or any webmail"
        >
          <Copy className="w-4 h-4" /> Copy
        </button>
      </div>
    </div>
  );
}
